import { useState } from 'react'
import {
  ArrowLeft, ArrowRight, ChevronDown, Circle, Clock, Square, Triangle, Truck,
} from 'lucide-react'

export type LegendIcon =
  | 'arrow-right' | 'arrow-left' | 'triangle' | 'dot' | 'square' | 'clock' | 'truck'

export interface LegendRow {
  icon: LegendIcon
  color: string
  label: string
  count: number
}

export interface MapLegendCardProps {
  title: string
  rows: LegendRow[]
  footer?: string
  /** Start collapsed. The card remembers the operator's choice for the session. */
  defaultCollapsed?: boolean
}

const ICON: Record<LegendIcon, typeof Circle> = {
  'arrow-right': ArrowRight,
  'arrow-left': ArrowLeft,
  triangle: Triangle,
  dot: Circle,
  square: Square,
  clock: Clock,
  truck: Truck,
}

// Marker-shape rows read better filled; directional/time glyphs stay stroked. The truck
// is stroked too -- filled solid at 14 px it collapses into a blob and loses the outline
// that makes it recognisable as the thing on the map.
const FILLED: ReadonlySet<LegendIcon> = new Set(['triangle', 'dot', 'square'])

export function MapLegendCard({ title, rows, footer, defaultCollapsed = false }: MapLegendCardProps) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed)

  // Collapsed still carries the fleet total. A legend that folds down to nothing but a
  // word makes the operator open it to answer "how many trucks am I looking at" -- which
  // is the question the card exists for.
  const total = rows.reduce((n, r) => n + r.count, 0)

  return (
    <div className="w-[212px] rounded-[12px] border border-[rgba(15,23,42,0.07)] bg-white/95 shadow-[0_2px_8px_rgba(15,23,42,0.08)] backdrop-blur-sm">
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        aria-expanded={!collapsed}
        title={collapsed ? 'Show the legend' : 'Hide the legend'}
        className="flex w-full items-center gap-2 rounded-[12px] px-3 py-2.5 text-left transition-colors hover:bg-[rgba(15,23,42,0.03)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]"
      >
        <span className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#94A3B8]">
          {title}
        </span>
        {collapsed && (
          <span className="font-mono text-[11px] tabular-nums text-[#94A3B8]">{total}</span>
        )}
        <ChevronDown
          className={`ml-auto h-3.5 w-3.5 shrink-0 text-[#94A3B8] transition-transform ${
            collapsed ? '-rotate-90' : ''
          }`}
        />
      </button>

      {!collapsed && (
        <div className="px-3 pb-3">
          <ul className="space-y-1.5">
            {rows.map((row, i) => {
              const Icon = ICON[row.icon]
              return (
                <li key={`${row.label}-${i}`} className="flex items-center gap-2">
                  <Icon
                    className="h-3.5 w-3.5 shrink-0"
                    style={{ color: row.color }}
                    fill={FILLED.has(row.icon) ? 'currentColor' : 'none'}
                  />
                  <span className="text-[12px] text-[#0F172A]">{row.label}</span>
                  <span className="ml-auto font-mono text-[11px] tabular-nums text-[#94A3B8]">{row.count}</span>
                </li>
              )
            })}
          </ul>
          {footer ? (
            <div className="mt-2 border-t border-[rgba(15,23,42,0.07)] pt-2 text-[10px] text-[#94A3B8]">
              {footer}
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}
